import { type AnimationState, type ChartElement, chartLayout } from "@cva/shared";
import type { ReactNode } from "react";
import { boxStyle, dropShadowCss, fontFamilyCss, joinFilters } from "./styles";

/**
 * Chart element: the geometry from `chartLayout` as plain SVG (vector paths
 * and real text), so the print PDF stays sharp and searchable.
 */
export function renderChart(element: ChartElement, state: AnimationState): ReactNode {
  const box = boxStyle(element, state);
  const scene = chartLayout(element);
  return (
    <div
      key={element.id}
      data-element-id={element.id}
      style={{
        ...box,
        filter: joinFilters(box.filter as string | undefined, dropShadowCss(element.shadow)),
      }}
    >
      <svg
        viewBox={`0 0 ${scene.width} ${scene.height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, display: "block", overflow: "visible" }}
        fontFamily={fontFamilyCss(scene.fontFamily)}
        role="img"
        aria-label={element.title || "Diagramm"}
      >
        {scene.shapes.map((shape, index) => {
          const key = index;
          switch (shape.kind) {
            case "path":
              return (
                <path
                  key={key}
                  d={shape.d}
                  fill={shape.fill ?? "none"}
                  fillOpacity={shape.fillOpacity}
                  stroke={shape.stroke}
                  strokeWidth={shape.strokeWidth}
                  strokeLinecap={shape.stroke ? "round" : undefined}
                  strokeLinejoin={shape.stroke ? "round" : undefined}
                />
              );
            case "circle":
              return <circle key={key} cx={shape.cx} cy={shape.cy} r={shape.r} fill={shape.fill} />;
            case "line":
              return (
                <line
                  key={key}
                  x1={shape.x1}
                  y1={shape.y1}
                  x2={shape.x2}
                  y2={shape.y2}
                  stroke={shape.stroke}
                  strokeWidth={shape.width}
                  shapeRendering="geometricPrecision"
                />
              );
            case "text":
              return (
                <text
                  key={key}
                  x={shape.x}
                  y={shape.y}
                  fontSize={shape.size}
                  fontWeight={shape.weight}
                  textAnchor={shape.anchor}
                  dominantBaseline="central"
                  fill={shape.fill}
                >
                  {shape.text}
                </text>
              );
          }
          return null;
        })}
      </svg>
    </div>
  );
}
